import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from 'src/shared/helpers/utils';

type ButtonRoundness = 'Default' | 'Round';

const buttonRoundnessClasses: Record<ButtonRoundness, string> = {
    Default: '',
    Round: 'rounded-full',
};

const buttonVariants = cva(
    'inline-flex items-center justify-center whitespace-nowrap text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
    {
        variants: {
            variant: {
                default: 'rounded-sm bg-primary text-primary-foreground hover:bg-primary/90',
                destructive: 'rounded-sm bg-destructive text-destructive-foreground hover:bg-general-destructive-reverse hover:text-white',
                outline:
                    'rounded-lg border border-input text-foreground hover:bg-unofficial-outline-hover hover:text-black hover:border-unofficial-border-3 hover:shadow-sm',
                secondary: 'rounded-sm bg-secondary text-secondary-foreground hover:bg-secondary/80',
                ghost: 'rounded-lg text-foreground hover:bg-unofficial-ghost-hover hover:text-black',
                link: 'rounded-lg text-foreground underline-offset-4 hover:underline',
            },
            size: {
                default: 'h-10 gap-2 px-4 py-2',
                sm: 'h-9 gap-1.5 px-3',
                lg: 'h-11 gap-2 px-8',
                mini: 'h-7 gap-1.5 px-2 py-1 text-xs',
                icon: 'h-10 w-10',
            },
        },
        compoundVariants: [
            { variant: 'link', size: 'default', className: 'h-auto px-4 py-2' },
            { variant: 'link', size: 'lg', className: 'h-auto px-6 py-2' },
            { variant: 'link', size: 'sm', className: 'h-auto px-3 py-1.5' },
            { variant: 'link', size: 'mini', className: 'h-auto px-2 py-1 text-xs' },
        ],
        defaultVariants: {
            variant: 'default',
            size: 'default',
        },
    }
);

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
    asChild?: boolean;
    roundness?: ButtonRoundness;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
    ({ className, variant, size, roundness = 'Default', asChild = false, ...props }, ref) => {
        const Comp = asChild ? Slot : 'button';
        return (
            <Comp className={cn(buttonVariants({ variant, size, className }), buttonRoundnessClasses[roundness])} ref={ref} {...props} />
        );
    }
);
Button.displayName = 'Button';

export { Button, buttonVariants };
